import type { CredentialResponse } from 'google-one-tap'
import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useRoute } from 'vue-router'

import { useConfig } from '@/composables/config'
import { usePopupAnalytics } from '@/composables/use-popup-analytics'
import { ROUTE_NAMES } from '@/router/constants'
import { useLoggerStore } from '@/store/logger'
import { useUserStore } from '@/store/user-store/user-store'
import { generateNonce } from '@/utils/crypto'
import { isIframe } from '@/utils/helpers'

import { useAppStore } from './app-store'
import { useNativeAppStore } from './native-app-store'
import { ONE_TAP_WEIGHT, useNotificationsQueue } from './notification-que/notifications-queue'

const PROMPT_MOMENT_TIMEOUT_MS = 10_000
const CANCELLED_ERROR_CODE = 'cancelled'

export const NATIVE_PROMOTE_SELECT_BY = 'native_promote'
const NATIVE_PROMPT_INTERFACE_TARGET = 'google-sign-in-prompt-native'

const CALLER_REPORTED_SELECT_BY = [
    'btn',
    'btn_confirm',
    'btn_add_session',
    'btn_confirm_add_session',
    NATIVE_PROMOTE_SELECT_BY,
]

export const useGoogleAuthStore = defineStore('googleAuth', () => {
    const { VUE_APP_GAUTH_REDIRECT, VUE_APP_GAUTH_CLID, VUE_APP_IOS_GAUTH_CLID } = useConfig()
    const loggerStore = useLoggerStore()
    const popupAnalytics = usePopupAnalytics('auth-notification')
    const userStore = useUserStore()
    const queue = useNotificationsQueue()
    const appStore = useAppStore()
    const nativeAppStore = useNativeAppStore()
    const route = useRoute()
    const onFinish = ref<((isSuccess: boolean, errorMessage?: string) => void) | null>(null)
    const initialized = ref(false)
    const promptDisplayed = ref(false)
    /*
      FedCM reports no display moment at all, so a dismissed moment is the only evidence one tap was really on screen
      (it fires for a user action on a visible prompt or for a returned credential).
      Sticky for the whole visit. useAutoSignInPrompt on the profile page stands down on it; the in-game
      nudge deliberately does not - see useLoginNudge.
    */
    const promptDismissedThisVisit = ref(false)
    /*
      The nonce the app was asked to sign with, and which entry point asked. Both native paths - the
      sign in button and the promoted login - set it, and the credential that comes back on
      `native:auth.event` is exchanged against it. It lives here because that credential can arrive
      long after, and outside of, the component that started the flow.
    */
    const nativeRequest = ref<{ nonce: string; selectBy: string }>()
    let momentTimeoutId: ReturnType<typeof setTimeout> | null = null

    function setOnFinishCallback(callback: (isSuccess: boolean, errorMessage?: string) => void) {
        onFinish.value = callback
    }

    function clearMomentTimeout() {
        if (momentTimeoutId) {
            clearTimeout(momentTimeoutId)
            momentTimeoutId = null
        }
    }

    function setPrompt() {
        // invoke prompt only for web
        if (window.google && !appStore.isAppEnv && !userStore.isAuthorized) {
            queue.request({
                name: 'auth-notification',
                weight: ONE_TAP_WEIGHT,
                onOpen: () => {
                    const oneTap = window.google?.accounts.id
                    if (!oneTap) {
                        queue.remove('auth-notification')
                        return
                    }

                    clearMomentTimeout()
                    // google normally always reports a moment, this only keeps the queue from stalling for the session
                    momentTimeoutId = setTimeout(() => {
                        momentTimeoutId = null
                        queue.remove('auth-notification')
                    }, PROMPT_MOMENT_TIMEOUT_MS)

                    oneTap.prompt((notification) => {
                        clearMomentTimeout()

                        // https://developers.google.com/identity/gsi/web/reference/js-reference#PromptMomentNotification
                        // display moments exist only without FedCM, under FedCM the prompt stays silent while shown
                        promptDisplayed.value =
                            typeof notification.isDisplayed === 'function' ? notification.isDisplayed() : false

                        const momentType = notification.getMomentType()
                        if (momentType === 'dismissed') {
                            promptDismissedThisVisit.value = true
                        }

                        if (promptDisplayed.value) {
                            loggerStore.logInterfaceEvents({
                                eventName: 'interface_show',
                                interface_target: 'google-sign-in-prompt',
                            })
                            popupAnalytics.shown({ trigger: 'prompt' })
                            return
                        }

                        if (momentType === 'dismissed') {
                            popupAnalytics.closed(`dismissed:${notification.getDismissedReason()}`)
                        } else if (momentType === 'skipped') {
                            popupAnalytics.closed(`skipped:${notification.getSkippedReason() || 'unknown'}`)
                        }
                        // a displayed prompt keeps the queue, every terminal moment frees it
                        queue.remove('auth-notification')
                    })
                },
                onClose: clearMomentTimeout,
            })
        }
    }

    function cancelPrompt() {
        queue.remove('auth-notification')
        window.google?.accounts.id.cancel()
    }

    function handleNativeMessage(data: unknown) {
        if (!data || typeof data !== 'object') {
            return
        }

        const message = data as { type?: string; payload?: Record<string, unknown> }

        if (message.type === 'native:auth.promoteLogin') {
            handlePromoteLoginResult(message.payload ?? {})
            return
        }

        if (message.type === 'native:auth.event') {
            handleNativeAuthResult(message.payload ?? {})
        }
    }

    function handlePromoteLoginResult({ shown, error }: Record<string, unknown>) {
        clearMomentTimeout()

        promptDisplayed.value = shown === true

        loggerStore.logInterfaceEvents({
            eventName: promptDisplayed.value ? 'interface_show' : 'interface_hide',
            interface_target: NATIVE_PROMPT_INTERFACE_TARGET,
        })

        if (error) {
            if (error !== CANCELLED_ERROR_CODE) {
                console.error('Native google login promotion error: ', error)
                onFinish.value?.(false, typeof error === 'string' ? error : JSON.stringify(error))
            }
            onFinish.value = null
        }

        if (!promptDisplayed.value) {
            promptDismissedThisVisit.value = true
            queue.remove('auth-notification')
        }
    }

    /** The app's answer to either native sign in request - the button or the promoted prompt. */
    function handleNativeAuthResult({ credential, errorCode }: Record<string, unknown>) {
        if (typeof credential !== 'string') {
            if (errorCode !== CANCELLED_ERROR_CODE) {
                if (appStore.isDebugMode) {
                    console.error(JSON.stringify({ credential, errorCode }), 'handleMessage from NATIVE')
                }
                onFinish.value?.(
                    false,
                    `Credential or nonce is missing in the authentication response ${errorCode || ''}`,
                )
            }
            onFinish.value = null
            queue.remove('auth-notification')
            return
        }

        exchangeCredential({
            credential,
            nonce: nativeRequest.value?.nonce,
            selectBy: nativeRequest.value?.selectBy ?? 'native',
        })
    }

    const unsubscribeNativeMessages = nativeAppStore.addMessageHandler(handleNativeMessage)

    function dispose() {
        unsubscribeNativeMessages()
        clearMomentTimeout()
    }

    async function requestNativeSignIn() {
        try {
            const { rawNonce } = await generateNonce()
            nativeRequest.value = { nonce: rawNonce, selectBy: 'btn' }

            nativeAppStore.postMessage('googleAuth.event', {
                nonce: rawNonce,
                clid: VUE_APP_GAUTH_CLID,
                iosClid: appStore.isNativeIosEnv ? VUE_APP_IOS_GAUTH_CLID : undefined,
            })
        } catch (e) {
            onFinish.value?.(false, (e as Error)?.message || String(e))
            onFinish.value = null
        }
    }

    async function maybePromoteNativeLogin(onResult?: (isSuccess: boolean, errorMessage?: string) => void) {
        if (route.name === ROUTE_NAMES.EXPORT_GAME_PAGE || userStore.isAuthorized || !appStore.isNativeAndroid) {
            return
        }

        const { rawNonce } = await generateNonce()
        nativeRequest.value = { nonce: rawNonce, selectBy: NATIVE_PROMOTE_SELECT_BY }

        queue.request({
            name: 'auth-notification',
            weight: ONE_TAP_WEIGHT,
            onOpen: () => {
                clearMomentTimeout()
                momentTimeoutId = setTimeout(() => {
                    momentTimeoutId = null
                    queue.remove('auth-notification')
                }, PROMPT_MOMENT_TIMEOUT_MS)

                if (onResult) {
                    onFinish.value = onResult
                }

                nativeAppStore.postMessage('googleAuth.promoteLogin', {
                    nonce: rawNonce,
                    clid: VUE_APP_GAUTH_CLID,
                })
            },
            onClose: clearMomentTimeout,
        })
    }

    /* should be used only once as google initialize should be invoked only once! -> see more https://developers.google.com/identity/gsi/web/reference/js-reference */
    async function initGlobalSignIn() {
        if (!window.google || initialized.value) {
            return
        }

        const { hashedNonce, rawNonce } = await generateNonce()

        const commonOptions = {
            client_id: VUE_APP_GAUTH_CLID,
            auto_select: true,
            cancel_on_tap_outside: false,
            use_fedcm_for_prompt: true,
            nonce: hashedNonce,
        }

        const temporaryDisableRedirectInFrames = true
        if (isIframe() && !temporaryDisableRedirectInFrames) {
            // When playgama is embedded on 3d party domain with COOP enabled
            // GSI cannot communicate from popup back to opener window
            window.google.accounts.id.initialize({
                ...commonOptions,
                ux_mode: 'redirect',
                login_uri: VUE_APP_GAUTH_REDIRECT,
            })
        } else {
            window.google.accounts.id.initialize({
                ...commonOptions,
                ux_mode: 'popup',
                callback: (response) => {
                    handleAuthentication(response, rawNonce)
                },
            })
        }
        initialized.value = true
    }

    function handleAuthentication({ select_by, credential }: CredentialResponse, nonce: string) {
        return exchangeCredential({ credential, nonce, selectBy: select_by })
    }

    async function exchangeCredential({
        credential,
        nonce,
        selectBy,
    }: {
        credential: string
        nonce: string | undefined
        selectBy: string
    }) {
        let errorMessage: string | undefined

        try {
            const res = await fetch(VUE_APP_GAUTH_REDIRECT, {
                method: 'POST',
                headers: { 'content-type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    credential,
                    nonce,
                }),
            })
            if (res.status > 399) {
                const parsed = await res.json()
                errorMessage = appStore.isDebugMode ? JSON.stringify(parsed) : parsed.message
            }
        } catch (e) {
            console.error('Google authentication error: ', e)
            errorMessage = (e as Error)?.message || String(e)
        }

        await userStore.fetchUserInfo()

        if (!CALLER_REPORTED_SELECT_BY.includes(selectBy)) {
            // log for prompt sign in attempts
            // the flows with a caller of their own report through useAuthResult instead
            loggerStore.logEvent({
                event: 'custom_event',
                eventName: userStore.isAuthorized ? 'auth_success' : 'auth_failed',
                dimension1: selectBy,
                dimension2: 'google',
                dimension3: errorMessage?.substring(0, 100),
            })
        }

        loggerStore.logEvent({
            event: 'custom_event',
            eventName: 'login',
            action: userStore.isAuthorized ? 'success' : 'fail',
        })

        if (promptDisplayed.value && userStore.isAuthorized) {
            promptDisplayed.value = false
        }

        onFinish.value?.(userStore.isAuthorized, errorMessage)
        onFinish.value = null
    }

    return {
        initGlobalSignIn,
        setPrompt,
        cancelPrompt,
        maybePromoteNativeLogin,
        requestNativeSignIn,
        setOnFinishCallback,
        dispose,
        initialized,
        promptDisplayed,
        promptDismissedThisVisit,
    }
})
